// Kid-proof joint descriptions + anatomical triplets — one of each for every canonical joint.
// The triplet is the set of THREE body points that define the angle (anatomy primer).
// The meaning is a plain-English sentence a 10-year-old can read.
// Paired with PHASE (when during the rep the angle is measured) so each row is one
// complete, self-explanatory row.

// point ① — point ② (vertex) — point ③
const JOINT_TRIPLET = {
  elbow:    'shoulder — elbow — wrist',
  shoulder: 'elbow — shoulder — hip',
  knee:     'hip — knee — ankle',
  hip:      'shoulder — hip — knee',
  back:     'shoulder — hip — ankle',
  ankle:    'knee — ankle — foot',
  wrist:    'elbow — wrist — fingers',
  neck:     'ear — shoulder — hip',
};

const JOINT_MEANING = {
  elbow:    'how bent your arm is · 180° = arm straight like a stick · 90° = arm bent like an L · smaller = more bent',
  shoulder: 'how far your arm is from your body · 0° = arm hanging at your side · 90° = arm out like a T-pose · 180° = arm reaching up to the sky',
  knee:     'how bent your leg is · 180° = leg straight · 90° = leg bent like sitting in a chair · smaller = deeper squat',
  hip:      'the fold between your upper body and your thigh · 180° = standing tall · smaller = folded more forward at the hips',
  // BACK re-written to match physical reality: at the bottom of a squat, BACK can
  // never be 180° because the hip moves behind the ankle. 180° is the STARTING
  // position (standing tall), not the rep-bottom target.
  back:     'how stacked your upper body stays over your feet · 180° when you\'re standing tall · naturally drops during the rep as your hips shift · smaller = pitched more forward over your feet',
  ankle:    'how much your shin tilts over your foot · 90° = shin straight up · smaller = shin tilting forward (knees over toes)',
  wrist:    'how bent your wrist is · 180° = hand lined up with your forearm · smaller = wrist bent',
  neck:     'how your head lines up with your upper body · 180° = head stacked over shoulders · smaller = head tilted',
};

const PHASE_SUFFIX = {
  at_bottom:  'measured at the bottom of the rep',
  at_top:     'measured at the top of the rep',
  throughout: 'must hold the whole rep (not just one point)',
};

const normalize = (label) => String(label || '').toLowerCase().trim().replace(/s$/, '');

// Anatomical triplet for display under the joint name. Empty string for unknown labels.
export const jointTriplet = (label) => JOINT_TRIPLET[normalize(label)] || '';

// Kid-proof sentence for the bracket — meaning + phase folded into one readable line.
export const jointDescription = (label, phase) => {
  const key = normalize(label);
  const meaning = JOINT_MEANING[key];
  if (!meaning) return '';
  const suffix = PHASE_SUFFIX[phase];
  return suffix ? `${meaning} · ${suffix}` : meaning;
};

// View-eligibility logic moved to /lib/metricsRegistry.js (single source of
// truth, mirrored on the backend in /backend/metrics_registry.py).
// Don't duplicate the SIDE_ONLY/FRONT_ONLY sets here — import the registry
// helpers directly where needed.
